import { Link } from "react-router-dom";
import { PencilLine } from "lucide-react";
import DOMPurify from "dompurify";
import { useAuth } from "@/context/AuthContext";
import { hasRealDescription } from "@/lib/format";
import { looksHtml } from "@/components/RichContent";
import { sanitizeHtml } from "@/components/RichTextEditor";

// Who can edit a listing, and where they edit it.
const ownerPath = (user, item) => {
  if (!user || !item) return null;
  if (["admin", "staff"].includes(user.role)) return "/admin";
  const ids = [item.enhancer_id, item.owner_id, item.facilitator_id].filter(Boolean);
  if (ids.includes(user.id)) return "/enhancer";
  const loc = item.center_location_id || item.location_id || (item.owner_type === "location" ? item.owner_id : "");
  if (user.manages_location_id && loc && loc === user.manages_location_id) return "/manager";
  return null;
};

// Shows the listing description; owners get a prompt to add one, visitors get warm fallback copy.
export default function ListingDescription({ text, item, kind = "offering", html = false, className = "", testid = "listing-description" }) {
  const { user } = useAuth();
  if (hasRealDescription(text)) {
    if (looksHtml(text)) return <div className={`rich-content ${className}`} data-testid={testid} dangerouslySetInnerHTML={{ __html: sanitizeHtml(String(text)) }} />;
    return html
      ? <div className={className} data-testid={testid} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(text).replace(/\n/g, "<br/>")) }} />
      : <p className={`whitespace-pre-line ${className}`} data-testid={testid}>{text}</p>;
  }
  const edit = ownerPath(user, item);
  if (edit) {
    return (
      <Link to={edit} onClick={(e) => e.stopPropagation()} data-testid={`${testid}-owner-prompt`}
        className="mt-2 flex items-start gap-2 rounded-xl border border-dashed border-[#D98E73]/60 bg-[#D98E73]/[0.06] px-3 py-2 text-xs text-[#B5794E] hover:bg-[#D98E73]/[0.12] transition-colors">
        <PencilLine size={14} className="shrink-0 mt-0.5" />
        <span><b>Add your description.</b> Tell visitors what this {kind} offers and who it's for — listings with a description book far more often.</span>
      </Link>
    );
  }
  return (
    <p className={`${className} italic text-[#8FA89B]`} data-testid={`${testid}-fallback`}>
      Full details for this {kind} are on the way. Reach out with any questions — we're happy to help you choose.
    </p>
  );
}
